import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { UpOutlined } from '@ant-design/icons';
import { FloatButton } from 'antd';
import SectionTitle from 'component/section-title/sectionTitle';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDebounce } from 'tools/hooks/debounce.hook';
import { useActiveEvent } from 'tools/hooks/useActiveEvent.hook';
import { Form, Switch, Typography, message } from 'ui';

import { isPublishingEventSelector } from 'selectors/eventsSlice.selector';
import { isOpenedSiderSelector } from 'selectors/systemSlice.selector';
import { useLazyGetUserEventsQuery } from 'services/user-management/userManagementApiService';
import { setEditingEventId, setIsPublishingEvent } from 'slices/eventsSlice';
import { useAppDispatch, useAppSelector } from 'store/index';

import EventPopover from 'containers/event-popover';
import EventsEmptyState from 'containers/events-empty-state';
import EventsLoadingState from 'containers/events-loading-state/eventsLoadingState';

import { UserEventType } from 'types/core/userTypes';
import { EventWithMetricsType } from 'types/events/eventTypes';

// import Pagination from '../../component/pagination';
import s from './Events.module.scss';
import EventListBlockView from './event-list-block-view';
import EventsHeader from './events-header';
import EventsListTableView from './events-list-table-view';
import { EventFilterType, SortByEnum, initialFilter } from './events.utils';

const Events: FC = () => {
  const [form] = Form.useForm();
  const isOpenedSidebar = useAppSelector(isOpenedSiderSelector);
  const searchEventName = Form.useWatch('eventName', form);
  const [getUserEvents, { data: userEvents, isFetching }] = useLazyGetUserEventsQuery();
  const [sortBy, setSortBy] = useState<SortByEnum>(SortByEnum.PUBLISHED_DATE);
  const [requestFilter, setRequestFilter] = useState<EventFilterType>({
    ...initialFilter,
    PageSize: 7,
  }); // Ensure PageSize is set
  const [isTableView, setIsTableView] = useState(false);
  const debouncedSearchEventName = useDebounce(searchEventName, 500) as string;
  const dispatch = useAppDispatch();
  const isPublishEvent = useAppSelector(isPublishingEventSelector);
  // const [currentEventId, setCurrentEventId] = useState<string>(); // Replaced by hook
  const activeEvent = useActiveEvent(); // Use the hook
  const [events, setEvents] = useState<EventWithMetricsType[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searched, setSearched] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [hidden, setIsHidden] = useState<boolean>(() => {
    const stored = localStorage.getItem('hidePastEvent');
    return stored === 'true'; // Convert from string to boolean
  });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    const scrollElement = document.getElementById('scrollableTableDiv');
    scrollElement?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const scrollElement = document.getElementById('scrollableTableDiv');
    if (!scrollElement) return;

    const handleScroll = () => {
      setShowScrollTop(scrollElement.scrollTop > 100);
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, []);

  // const showedElements =
  //   Number(userEvents?.events?.length) +
  //   (Number(userEvents?.pageNumber) - 1) * Number(requestFilter.PageSize);

  // const handlePageChange = useCallback((PageNumber: number) => {
  //   setRequestFilter((prevState) => ({
  //     ...prevState,
  //     PageNumber,
  //   }));
  // }, []);

  const DEFAULT_PAGE_SIZE = 7;
  const prevSearchRef = useRef('');

  // Effect for handling search term changes (new search or clearing search)
  useEffect(() => {
    // New search initiated
    if (
      debouncedSearchEventName &&
      debouncedSearchEventName !== prevSearchRef.current &&
      debouncedSearchEventName.length >= 3
    ) {
      prevSearchRef.current = debouncedSearchEventName;
      setSearching(true);
      setEvents([]); // Clear previous results
      setRequestFilter((prev) => ({
        ...prev,
        PageNumber: 1,
        Search: debouncedSearchEventName,
        PageSize: DEFAULT_PAGE_SIZE, // Use defined page size for search
      }));
      setHasLoaded(false); // Will trigger the data fetching useEffect
    } else if (
      searchEventName === '' &&
      debouncedSearchEventName !== prevSearchRef.current &&
      debouncedSearchEventName === '' &&
      searched
    ) {
      prevSearchRef.current = debouncedSearchEventName;
      setSearching(true);
      setEvents([]); // Clear previous results
      setRequestFilter(() => ({
        ...initialFilter, // Reset to initial
        SortBy: sortBy, // Keep current sort
        ExcludeArchivedEvents: !hidden, // Keep current filter
        PageNumber: 1,
        PageSize: DEFAULT_PAGE_SIZE,
        Search: '',
      }));
      setSearched(false); // No longer in search mode
      setHasLoaded(false); // Will trigger the data fetching useEffect
    }
  }, [debouncedSearchEventName, searchEventName, sortBy, hidden, searched]); // Removed getUserEvents, events, userEvents

  // Effect for handling sortBy and hidden changes
  useEffect(() => {
    setEvents([]); // Clear existing events
    setRequestFilter((prevState) => ({
      ...prevState,
      PageNumber: 1, // Reset to page 1
      SortBy: sortBy,
      ExcludeArchivedEvents: !hidden,
      // Search term is preserved from requestFilter's current state
    }));
    setHasLoaded(false); // This will trigger the data fetching useEffect
  }, [sortBy, hidden]);

  // Centralized data fetching effect based on requestFilter changes or when !hasLoaded
  useEffect(() => {
    // Only fetch if not already loading and hasLoaded is false (meaning a change requires data reload)
    if (!isFetching && !hasLoaded) {
      // requestFilter already holds the correct Search term (either from search input or empty for non-search)
      // and PageNumber (1 for new loads/filter changes)

      const apiFilter = {
        ...requestFilter, // Contains PageNumber, PageSize, Search, SortBy, ExcludeArchivedEvents
        IncludeMetrics: true,
      };

      // Set searching to true for any Page 1 fetch (initial load, new search, filter change)
      // searching state is also used by EventsHeader to show loading indicator on search input
      if (apiFilter.PageNumber === 1) {
        setSearching(true);
      }

      getUserEvents(apiFilter).then((response) => {
        if (response?.data) {
          // For Page 1, always set new events. LoadMore handles appending.
          if (apiFilter.PageNumber === 1) {
            setEvents(response.data.events);
          }
          // If this effect were to handle loadMore (PageNumber > 1), it would append:
          // else { setEvents(prevEvents => [...prevEvents, ...response.data.events]); }

          setHasMore(response.data.nextPage !== null);
          // Update `searched` state based on whether a search term was effectively used from requestFilter
          setSearched(apiFilter.Search !== '');
        } else {
          // Handle error case or no data: clear events for page 1, stop pagination
          if (apiFilter.PageNumber === 1) setEvents([]);
          setHasMore(false);
        }
        setHasLoaded(true); // Mark as loaded after fetch attempt (success or fail)
        // Stop searching indicator, especially important if it was a page 1 load
        if (apiFilter.PageNumber === 1) {
          setSearching(false);
        }
      });
    }
  }, [requestFilter, hasLoaded, getUserEvents, isFetching]); // Dependencies refined: removed debouncedSearchEventName, searched

  const loadMoreData = () => {
    if (!isFetching && hasMore) {
      // Only load more if not fetching and hasMore is true
      const nextPageNumber = requestFilter.PageNumber! + 1;
      // setRequestFilter((prev) => ({ // This will trigger the useEffect above, leading to potential double fetches or race conditions.
      //   ...prev,
      //   PageNumber: nextPageNumber,
      // }));

      // Direct call for loadMore to avoid re-triggering the main data fetching useEffect unnecessarily for pagination.
      const filterForAPI = {
        ...requestFilter,
        PageNumber: nextPageNumber,
        IncludeMetrics: true,
        // Search term is already in requestFilter.Search
      };

      getUserEvents(filterForAPI).then((response) => {
        if (response && response.data && response.data.events) {
          setEvents((prevEvents) => [...prevEvents, ...response.data!.events]);
          setHasMore(response.data.nextPage !== null);
          // Update requestFilter's PageNumber silently for the next loadMore call, without re-triggering main useEffect
          setRequestFilter((prev) => ({ ...prev, PageNumber: nextPageNumber }));
        }
      });
    }
  };

  // useEffect(() => { // This is now handled by useActiveEvent hook
  //   const storedEvent = localStorage.getItem('currentEvent');
  //   if (storedEvent) {
  //     setCurrentEventId(JSON.parse(storedEvent).eventId);
  //   }
  // }, []);

  useEffect(() => {
    if (isPublishEvent) {
      message.success('Event successfully submitted for publication!');

      setTimeout(() => {
        dispatch(setIsPublishingEvent(false));
      }, 2000);
    }
  }, [dispatch, isPublishEvent]);

  const handleSetActiveEvent = useCallback(
    (event: UserEventType) => {
      dispatch(setEditingEventId(event?.eventId));
    },
    [dispatch],
  );

  return (
    <div id="scrollableTableDiv" className={s.wrapper} ref={scrollContainerRef}>
      {showScrollTop && !isOpenedSidebar && (
        <FloatButton
          onClick={scrollToTop}
          type="primary"
          style={{ right: 24, bottom: 24 }}
          icon={<UpOutlined />}
        />
      )}
      <div className={s.headerRow}>
        <SectionTitle
          label="common_my_events"
          className={s.title}
          counter={isFetching ? undefined : userEvents?.totalRecords}
        />
        <EventPopover handleEvent={handleSetActiveEvent} />
      </div>
      <Form form={form}>
        <EventsHeader
          searching={searching}
          sortBy={sortBy}
          setSortBy={setSortBy}
          isBlockView={isTableView}
          setIsBlockView={setIsTableView}
        />
        <Form.Item name="hidePastEvent" initialValue={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <Switch
              loading={isFetching}
              onChange={(checked) => {
                setIsHidden(checked);
                localStorage.setItem('hidePastEvent', String(checked));
              }}
              value={hidden}
            />
            <Typography type="main">{hidden ? 'Hide' : 'Show'} passed events</Typography>
          </div>
        </Form.Item>
      </Form>
      {isTableView && events?.length > 0 && (
        <div className={s.scrollWrapper}>
          <InfiniteScroll
            dataLength={events.length!}
            next={loadMoreData}
            hasMore={hasMore}
            loader={<EventsLoadingState size={250} />}
            scrollableTarget="scrollableTableDiv">
            <EventsListTableView list={events} />
          </InfiniteScroll>
        </div>
      )}
      {!isTableView && events?.length > 0 && events && (
        <div className={s.scrollWrapper}>
          <InfiniteScroll
            dataLength={events.length!}
            next={loadMoreData}
            hasMore={hasMore}
            loader={<EventsLoadingState size={250} />}
            scrollableTarget="scrollableTableDiv">
            <EventListBlockView
              currentEventId={activeEvent?.eventId ?? ''} // Use eventId from hook
              list={events}
              isLoading={isFetching}
            />
          </InfiniteScroll>
        </div>
      )}
      {!isFetching && events?.length === 0 && (
        <div className={s.empty}>
          <EventsEmptyState />
        </div>
      )}
      {isFetching && events?.length === 0 && !isTableView && (
        <div className={s.loadingWrapper}>
          <EventsLoadingState size={240} />
        </div>
      )}
      {/* {showedElements > 0 && (
        <Pagination
          totalElements={userEvents?.totalRecords}
          showedElements={showedElements}
          currentPage={requestFilter?.PageNumber || 0}
          total={userEvents?.totalPages}
          handlePageChange={handlePageChange}
          handleSizeChange={(size) =>
            setRequestFilter((prevValue) => ({ ...prevValue, PageSize: size }))
          }
        />
      )} */}
    </div>
  );
};

export default Events;
